// src/components/Pimgy/TransactionHistory.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { formatDateToDDMMYYYY, getTodayYYYYMMDD } from '../../utils/dateUtils';
import '../AdminDashboard/ExpenseManager.css';

const TransactionHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTransactions();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [transactions, filterDateFrom, filterDateTo]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/pimgy/transaction-history`);
      setTransactions(response.data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      alert('Failed to load transaction history');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...transactions];

    if (filterDateFrom) {
      filtered = filtered.filter(t => t.transaction_date >= filterDateFrom);
    }
    if (filterDateTo) {
      filtered = filtered.filter(t => t.transaction_date <= filterDateTo);
    }

    setFilteredTransactions(filtered);
  };

  const handleFilter = () => {
    applyFilters();
  };

  const clearFilters = () => {
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  const printTransactionHistory = () => {
    if (filteredTransactions.length === 0) {
      alert('No transactions to print.');
      return;
    }

    const totalReceived = filteredTransactions.reduce((sum, t) => sum + parseFloat(t.received || 0), 0);
    const totalWithdrawn = filteredTransactions.reduce((sum, t) => sum + parseFloat(t.withdrawn || 0), 0);
    const dateRange = filterDateFrom && filterDateTo 
      ? `From: ${formatDateToDDMMYYYY(filterDateFrom)} - To: ${formatDateToDDMMYYYY(filterDateTo)}`
      : 'All Transactions';

    const historyContent = `
<style>
  body { font-family: Arial, sans-serif; margin: 20px; padding: 20px; }
  .header { text-align: center; font-weight: bold; font-size: 1.5em; margin-bottom: 5px; }
  .sub-header { text-align: center; font-size: 1em; margin-bottom: 15px; }
  .date-range { text-align: center; margin-bottom: 20px; font-weight: bold; }
  table { width: 100%; border-collapse: collapse; margin-top: 20px; }
  th, td { border: 1px solid #000; padding: 8px; text-align: left; }
  th { background-color: #f0f0f0; font-weight: bold; }
  .right { text-align: right; }
  .total-row { background-color: #f0f0f0; font-weight: bold; }
  .footer { text-align: center; margin-top: 30px; font-size: 0.9em; }
</style>

<div class="header">M/S SHREE SAI FINANCE AND LEASING (R) & Leasing</div>
<div class="sub-header">Rudrawadi</div>
<div class="date-range">Pigmy Collection - Transaction History</div>
<div class="date-range">${dateRange}</div>

<table>
  <thead>
    <tr>
      <th>Date</th>
      <th>Registration No</th>
      <th>Name</th>
      <th>Finance Name</th>
      <th class="right">Received (₹)</th>
      <th class="right">Withdrawn (₹)</th>
      <th>Payment Mode</th>
      <th>Transaction ID</th>
    </tr>
  </thead>
  <tbody>
    ${filteredTransactions.map(t => `
      <tr>
        <td>${formatDateToDDMMYYYY(t.transaction_date)}</td>
        <td>${t.registration_no}</td>
        <td>${t.name}</td>
        <td>${t.finance_name || '-'}</td>
        <td class="right">${Number(t.received || 0).toFixed(2)}</td>
        <td class="right">${Number(t.withdrawn || 0).toFixed(2)}</td>
        <td>${t.payment_mode || '-'}</td>
        <td>${t.transaction_id || '-'}</td>
      </tr>
    `).join('')}
    <tr class="total-row">
      <td colspan="4" style="text-align: right;"><strong>TOTAL:</strong></td>
      <td class="right"><strong>₹ ${totalReceived.toFixed(2)}</strong></td>
      <td class="right"><strong>₹ ${totalWithdrawn.toFixed(2)}</strong></td>
      <td colspan="2"></td>
    </tr>
  </tbody>
</table>

<div class="footer">
  Printed on: ${formatDateToDDMMYYYY(new Date().toISOString().split('T')[0])}
</div>
`;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(historyContent);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="transaction-history-section">
      <h3>Transaction History</h3>
      
      <div className="filters-container">
        <div className="filter-group">
          <label>From Date:</label>
          <input
            type="date"
            className="filter-input"
            value={filterDateFrom}
            onChange={(e) => setFilterDateFrom(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>To Date:</label>
          <input
            type="date"
            className="filter-input"
            value={filterDateTo}
            onChange={(e) => setFilterDateTo(e.target.value)}
          />
        </div>
        <div className="filter-buttons">
          <button className="clear-button" onClick={clearFilters}>
            Clear
          </button>
          <button className="print-button" onClick={printTransactionHistory}>
            Print
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>Loading transactions...</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="transactions-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Registration No</th>
                <th>Name</th>
                <th>Finance Name</th>
                <th>Received (₹)</th>
                <th>Withdrawn (₹)</th>
                <th>Payment Mode</th>
                <th>Transaction ID</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.length > 0 ? (
                filteredTransactions.map((t) => (
                  <tr key={t.id}>
                    <td>{formatDateToDDMMYYYY(t.transaction_date)}</td>
                    <td>{t.registration_no}</td>
                    <td>{t.name}</td>
                    <td>{t.finance_name || '-'}</td>
                    <td>₹{Number(t.received || 0).toFixed(2)}</td>
                    <td>₹{Number(t.withdrawn || 0).toFixed(2)}</td>
                    <td>{t.payment_mode || '-'}</td>
                    <td>{t.transaction_id || '-'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>
                    No transactions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TransactionHistory;

